import { useCallback, useEffect, useRef } from 'react'

/**
 * Debounced callback with a latest-args ref and unmount cleanup. Mirrors
 * `apps/gto-wizard-web/src/hooks/use-debounce.ts` — apps cannot import from each
 * other, and the shared `@gto/helpers` only ships the value-debounce variant,
 * which needs a write-back effect that fights URL-as-source-of-truth state.
 *
 * The returned `debounced` identity is stable across renders (the live
 * callback is read through a ref), so a pending timer survives the re-render
 * each keystroke causes.
 */
export function useDebouncedCallback<T extends unknown[]>(
  callback: (...args: T) => void,
  delay: number,
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastArgsRef = useRef<T | null>(null)
  const callbackRef = useRef(callback)

  useEffect(
    function syncCallbackRef() {
      callbackRef.current = callback
    },
    [callback],
  )

  useEffect(function clearPendingTimerOnUnmount() {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [])

  const debounced = useCallback(
    (...args: T) => {
      lastArgsRef.current = args

      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
      }

      timerRef.current = setTimeout(() => {
        if (lastArgsRef.current) {
          callbackRef.current(...lastArgsRef.current)
        }
      }, delay)
    },
    [delay],
  )

  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null

      if (lastArgsRef.current) {
        callbackRef.current(...lastArgsRef.current)
      }
    }
  }, [])

  return { debounced, flush }
}
